/**
 * InsightPro™ — Expense Analysis  (Multi-Entity Finance clients only)
 *
 * KPI strip (Total OpEx · % of Sales · vs Prior · vs LY · Top Category)
 * + composition bars by report_category
 * + top-10 GL accounts table
 * + 12-month trend (top categories)
 * + per-entity OpEx/Sales ratio table (RAG vs group median, click → GL drill).
 *
 * CoS is excluded from totals by default (toggle in header) so controllable
 * opex creep isn't drowned out by cost-of-sales scaling with revenue.
 *
 * Data: getExpenseAnalysis · getExpenseTrend · getExpenseEntities · getEntityPLDetail
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  fetchFinancePeriods, fetchFinanceBrands,
  fetchExpenseAnalysis, fetchExpenseTrend, fetchExpenseEntities,
  fetchEntityPLDetail,
} from '../utils/api';

// ── Number formatting (mirrors GroupPL/BS) ───────────────────
const fmtMoney = (v) => {
  if (v == null || isNaN(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return (v / 1_000_000).toFixed(2) + 'M';
  if (abs >= 1_000)     return (v / 1_000).toFixed(0) + 'K';
  return v.toFixed(0);
};
const fmtFull  = (v) => (v == null || isNaN(v)) ? '—' :
  v.toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtPct   = (v, digits = 1) => (v == null || isNaN(v)) ? '—' : (v * 100).toFixed(digits) + '%';
const fmtDelta = (v) => {
  if (v == null || isNaN(v)) return '—';
  const sign = v > 0 ? '+' : '';
  return sign + (v * 100).toFixed(1) + '%';
};

// OpEx-as-%-of-Sales RAG: lower is better.
//   ≤15% green · 15–25% amber · >25% red.
// Page-level toggle (includeCoS) raises baseline; we shift thresholds to keep
// "median ± 20%" the right comparison instead of an absolute number.
const ratioColor = (ratio, includeCoS) => {
  if (ratio == null || isNaN(ratio)) return 'var(--page-muted)';
  const t1 = includeCoS ? 0.55 : 0.15;
  const t2 = includeCoS ? 0.75 : 0.25;
  if (ratio <= t1) return 'var(--green-l)';
  if (ratio <= t2) return 'var(--amber)';
  return 'var(--red)';
};

// Per-entity vs group median — separate signal: are we worse than peers?
const ratioVsMedianColor = (ratio, median) => {
  if (ratio == null || median == null || median === 0) return 'var(--page-muted)';
  const dev = (ratio - median) / median;
  if (dev <= -0.10) return 'var(--green-l)';
  if (dev <=  0.20) return 'var(--amber)';
  return 'var(--red)';
};

const DELTA_BAD_COLOR  = 'var(--red)';     // opex up = bad
const DELTA_GOOD_COLOR = 'var(--green-l)'; // opex down = good
const deltaColor = (delta) => (delta == null || isNaN(delta) || delta === 0) ? 'var(--page-muted)' : (delta > 0 ? DELTA_BAD_COLOR : DELTA_GOOD_COLOR);

// 8-step palette for category series in trend + composition bars
const CAT_COLORS = ['#C89520', '#8FBE5F', '#5FB3C8', '#A87DC8', '#E07B7B', '#7BB1E0', '#D8C45F', '#A8A8A8'];

const S = {
  page:        { padding: '20px 28px', maxWidth: 1500 },
  header:      { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 20 },
  title:       { fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--page-text)', letterSpacing: '-0.02em' },
  sub:         { fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--page-muted)', marginTop: 4, letterSpacing: '0.1em', textTransform: 'uppercase' },
  selectorBar: { display: 'flex', gap: 12, alignItems: 'flex-end' },
  label:       { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 5 },
  select:      { background: 'var(--page)', border: '1px solid var(--card-border)', padding: '8px 12px', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--page-text)', outline: 'none', borderRadius: 2, minWidth: 140 },
  toggle:      { display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--page-muted)', cursor: 'pointer' },
  toggleBox:   { width: 28, height: 14, background: 'var(--card-border)', borderRadius: 8, position: 'relative', transition: 'background 0.15s' },
  toggleDot:   { position: 'absolute', top: 1, width: 12, height: 12, background: '#fff', borderRadius: '50%', transition: 'left 0.15s' },
  kpiGrid:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 },
  kpiCard:     { background: 'var(--card)', border: '1px solid var(--card-border)', borderRadius: 4, padding: '16px 20px' },
  kpiLabel:    { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 8 },
  kpiVal:      { fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--page-text)', lineHeight: 1 },
  kpiSub:      { fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)', marginTop: 6 },
  twoCol:      { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 },
  card:        { background: 'var(--card)', border: '1px solid var(--card-border)', borderRadius: 4, padding: '16px 20px', marginBottom: 16 },
  cardTitle:   { fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 12 },
  cardTotal:   { fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 700, color: 'var(--gold)', float: 'right' },
  table:       { width: '100%', borderCollapse: 'collapse' },
  th:          { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--page-muted)', padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid var(--card-border)', background: 'var(--page)' },
  thRight:     { textAlign: 'right' },
  td:          { padding: '9px 10px', fontSize: 12.5, color: 'var(--page-text)', borderBottom: '1px solid var(--card-border)', verticalAlign: 'middle' },
  tdRight:     { textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12 },
  tdMono:      { fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)' },
  errorBox:    { padding: 14, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--red)', background: 'rgba(176,40,40,0.08)', border: '1px solid rgba(176,40,40,0.2)', borderRadius: 2, marginBottom: 16 },
  loadingBox:  { padding: 28, textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)' },
  closeBtn:    { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'transparent', border: '1px solid var(--card-border)', padding: '4px 10px', cursor: 'pointer', color: 'var(--page-muted)', borderRadius: 2 },
  catRow:      { display: 'grid', gridTemplateColumns: '1fr 80px 60px 70px', alignItems: 'center', gap: 10, padding: '6px 0', cursor: 'pointer' },
  catLabel:    { display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 },
  catSwatch:   { width: 8, height: 8, borderRadius: 1, flexShrink: 0 },
  catName:     { fontSize: 12.5, color: 'var(--page-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  catBarTrack: { width: '100%', height: 6, background: 'var(--page)', border: '1px solid var(--card-border)', borderRadius: 1, overflow: 'hidden', marginTop: 2 },
  catBarFill:  { height: '100%' },
  catAmount:   { fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--page-text)', textAlign: 'right' },
  catPct:      { fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)', textAlign: 'right' },
  catDelta:    { fontFamily: 'var(--font-mono)', fontSize: 11, textAlign: 'right' },
  miniBar:     { display: 'inline-block', height: 5, background: 'var(--gold)', borderRadius: 1, marginLeft: 4 },
  filterRow:   { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12, fontSize: 12 },
  filterChip:  { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', background: 'rgba(200,149,32,0.10)', border: '1px solid rgba(200,149,32,0.25)', borderRadius: 2, fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--amber)' },
};

// ── Composition: horizontal bars per category ────────────────
function CategoryBars({ categories, max, palette, onClick }) {
  if (!categories.length) return <div style={S.loadingBox}>No expenses for this period.</div>;
  return (
    <div>
      {categories.map((c, i) => {
        const pct = max ? c.amount / max : 0;
        const color = palette[i % palette.length];
        return (
          <div
            key={c.reportCategory}
            style={S.catRow}
            onClick={() => onClick(c)}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(200,149,32,0.04)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <div>
              <div style={S.catLabel}>
                <span style={{ ...S.catSwatch, background: color }} />
                <span style={S.catName} title={c.reportCategory}>{c.reportCategory}</span>
              </div>
              <div style={S.catBarTrack}>
                <div style={{ ...S.catBarFill, width: (pct * 100) + '%', background: color }} />
              </div>
            </div>
            <div style={S.catAmount}>{fmtMoney(c.amount)}</div>
            <div style={S.catPct}>{fmtPct(c.pctOfSales)}</div>
            <div style={{ ...S.catDelta, color: deltaColor(c.delta) }}>
              {c.delta == null ? '—' : (c.delta > 0 ? '▲' : c.delta < 0 ? '▼' : '·')} {fmtDelta(c.deltaPct)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Trend: 12-month line per top-N categories ────────────────
function TrendChart({ trend, revenue, palette, topCategories }) {
  const periods = useMemo(() => {
    const set = new Set();
    trend.forEach(t => set.add(t.periodKey));
    return [...set].sort();
  }, [trend]);
  if (periods.length < 2) return <div style={S.loadingBox}>Need ≥ 2 periods to plot trend ({periods.length} available).</div>;

  // Build series: top categories from current month dominate
  const series = topCategories.map(c => {
    const points = periods.map(p => {
      const row = trend.find(t => t.periodKey === p && t.reportCategory === c.reportCategory);
      return { periodKey: p, amount: row ? row.amount : 0 };
    });
    return { reportCategory: c.reportCategory, points };
  });

  const allAmounts = series.flatMap(s => s.points.map(p => p.amount));
  const maxAmt = Math.max(...allAmounts, 1);

  const W = 800, H = 220, pad = { l: 56, r: 64, t: 14, b: 28 };
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;
  const xStep = innerW / (periods.length - 1);
  const xAt   = (i) => pad.l + i * xStep;
  const yAt   = (v) => pad.t + (1 - v / maxAmt) * innerH;

  // Right-axis line: OpEx total / Revenue per period
  const totalByPeriod = periods.map(p => {
    const t = trend.filter(r => r.periodKey === p).reduce((s, r) => s + r.amount, 0);
    const rev = revenue.find(r => r.periodKey === p)?.revenue || 0;
    return { periodKey: p, ratio: rev ? t / rev : null };
  });
  const ratioVals = totalByPeriod.map(t => t.ratio).filter(v => v != null);
  const maxRatio = Math.max(...ratioVals, 0.01);
  const yRatio   = (v) => v == null ? null : pad.t + (1 - v / maxRatio) * innerH;

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
        {/* gridlines */}
        {[0, 0.25, 0.5, 0.75, 1].map(g => {
          const y = pad.t + g * innerH;
          return <line key={g} x1={pad.l} x2={W - pad.r} y1={y} y2={y} stroke="var(--card-border)" strokeWidth={0.5} />;
        })}
        {/* x-axis labels */}
        {periods.map((p, i) => (
          <text key={p} x={xAt(i)} y={H - 8} fontSize={9} fill="var(--page-muted)" textAnchor="middle"
            fontFamily="var(--font-mono)">
            {p.slice(2)}
          </text>
        ))}
        {/* y-axis (left) labels */}
        {[0, 0.5, 1].map(g => {
          const v = maxAmt * (1 - g);
          return (
            <text key={g} x={pad.l - 6} y={pad.t + g * innerH + 3} fontSize={9} fill="var(--page-muted)"
              textAnchor="end" fontFamily="var(--font-mono)">{fmtMoney(v)}</text>
          );
        })}
        {/* y-axis (right) labels — ratio */}
        {[0, 0.5, 1].map(g => {
          const v = maxRatio * (1 - g);
          return (
            <text key={g} x={W - pad.r + 6} y={pad.t + g * innerH + 3} fontSize={9} fill="var(--gold)"
              textAnchor="start" fontFamily="var(--font-mono)">{(v * 100).toFixed(0)}%</text>
          );
        })}
        {/* category lines */}
        {series.map((s, i) => {
          const color = palette[i % palette.length];
          const d = s.points.map((pt, j) => `${j === 0 ? 'M' : 'L'} ${xAt(j)} ${yAt(pt.amount)}`).join(' ');
          return <path key={s.reportCategory} d={d} fill="none" stroke={color} strokeWidth={1.6} />;
        })}
        {/* ratio line — dashed gold */}
        <path
          d={totalByPeriod.map((t, i) => {
            const y = yRatio(t.ratio);
            return y == null ? '' : `${i === 0 ? 'M' : 'L'} ${xAt(i)} ${y}`;
          }).join(' ')}
          fill="none" stroke="var(--gold)" strokeWidth={1.5} strokeDasharray="3,3"
        />
      </svg>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 8, fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--page-muted)' }}>
        {series.map((s, i) => (
          <span key={s.reportCategory} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 10, height: 2, background: palette[i % palette.length], display: 'inline-block' }} />
            {s.reportCategory}
          </span>
        ))}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 10, height: 2, background: 'var(--gold)', display: 'inline-block', borderTop: '1px dashed var(--gold)' }} />
          Spend / Revenue (right axis)
        </span>
      </div>
    </div>
  );
}

export default function ExpenseAnalysis() {
  const [periods, setPeriods]    = useState([]);
  const [period,  setPeriod]     = useState(null);
  const [brands,  setBrands]     = useState({ brands: ['Consolidated'], canPickBrand: false });
  const [brand,   setBrand]      = useState('Consolidated');
  const [includeCoS, setIncludeCoS] = useState(false);
  const [analysis, setAnalysis]  = useState(null);
  const [trend,    setTrend]     = useState(null);
  const [entities, setEntities]  = useState(null);
  const [loading,  setLoading]   = useState(true);
  const [err,      setErr]       = useState(null);
  const [filters,  setFilters]   = useState({});  // { country, province, entityType, reportCategory, glAccountKey }
  const [drill,    setDrill]     = useState(null); // { entity, lines? }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, b] = await Promise.all([fetchFinancePeriods(), fetchFinanceBrands()]);
        if (cancelled) return;
        setPeriods(p.periods || []);
        setPeriod(p.latest || (p.periods?.[0]?.periodKey ?? null));
        setBrands(b);
        setBrand(b.canPickBrand ? 'Consolidated' : (b.brands[0] || 'Consolidated'));
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.error || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!period) return;
    let cancelled = false;
    setLoading(true); setErr(null); setDrill(null);
    const apiBrand = brand === 'Consolidated' ? 'all' : brand;
    Promise.all([
      fetchExpenseAnalysis(period, apiBrand, { ...filters, includeCoS }),
      fetchExpenseTrend   (period, apiBrand, { ...filters, includeCoS, months: 12 }),
      fetchExpenseEntities(period, apiBrand, { ...filters, includeCoS }),
    ])
      .then(([a, t, e]) => {
        if (cancelled) return;
        setAnalysis(a); setTrend(t); setEntities(e); setLoading(false);
      })
      .catch(e => { if (!cancelled) { setErr(e.response?.data?.error || e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [period, brand, includeCoS, filters]);

  const removeFilter = (k) => setFilters(f => { const out = { ...f }; delete out[k]; return out; });
  const clearFilters = () => setFilters({});

  const periodOptions = useMemo(() =>
    [...periods].sort((a, b) => (b.periodKey || '').localeCompare(a.periodKey || '')),
    [periods]
  );

  const totals     = analysis?.totals || {};
  const categories = analysis?.categories || [];
  const accounts   = analysis?.accounts || [];
  // When the user has drilled into a single category, narrow the GL list to match.
  const visibleAccounts = useMemo(() =>
    filters.reportCategory ? accounts.filter(a => a.reportCategory === filters.reportCategory) : accounts,
    [accounts, filters.reportCategory]
  );
  const topCats    = useMemo(() => categories.slice(0, 6), [categories]);
  const maxCat     = categories[0]?.amount || 0;

  // Median OpEx/Sales across entities (for vs-median RAG colouring)
  const entityRows = entities?.entities || [];
  const medianRatio = useMemo(() => {
    const vals = entityRows.map(e => e.ratio).filter(v => v != null && !isNaN(v) && isFinite(v));
    if (!vals.length) return null;
    vals.sort((a, b) => a - b);
    const mid = Math.floor(vals.length / 2);
    return vals.length % 2 ? vals[mid] : (vals[mid - 1] + vals[mid]) / 2;
  }, [entityRows]);

  const openEntityDrill = async (e) => {
    setDrill({ entity: e, lines: null });
    try {
      const data = await fetchEntityPLDetail(period, e.entityKey);
      const expenseTypes = includeCoS ? ['Cost of Sales', 'Other Expense'] : ['Other Expense'];
      const lines = (data.lines || [])
        .filter(l => expenseTypes.includes(l.accountType))
        .map(l => ({ ...l, amount: -l.amount }))   // flip sign for display
        .sort((a, b) => b.amount - a.amount);
      setDrill({ entity: e, lines });
    } catch (err) {
      setDrill(d => ({ ...(d || {}), error: err.response?.data?.error || err.message }));
    }
  };

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <div style={S.title}>Expense Analysis</div>
          <div style={S.sub}>
            {brand === 'Consolidated' ? 'Consolidated · all brands' : `${brand} only`}
            {' · '}{period || '—'}
            {' · '}{includeCoS ? 'CoS + OpEx' : 'OpEx only'}
            {filters.country     && ` · ${filters.country}`}
            {filters.province    && ` · ${filters.province}`}
            {filters.entityType  && ` · ${filters.entityType}`}
            {filters.reportCategory && ` · ${filters.reportCategory}`}
          </div>
        </div>
        <div style={S.selectorBar}>
          <label style={S.toggle} onClick={() => setIncludeCoS(v => !v)}>
            Include CoS
            <span style={{ ...S.toggleBox, background: includeCoS ? 'var(--gold)' : 'var(--card-border)' }}>
              <span style={{ ...S.toggleDot, left: includeCoS ? 15 : 1 }} />
            </span>
          </label>
          {brands.canPickBrand && (
            <div>
              <div style={S.label}>Brand</div>
              <select style={S.select} value={brand} onChange={e => setBrand(e.target.value)}>
                {brands.brands.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          )}
          <div>
            <div style={S.label}>Period</div>
            <select style={S.select} value={period || ''} onChange={e => setPeriod(e.target.value)}>
              {periodOptions.map(p => (
                <option key={p.periodKey} value={p.periodKey}>{p.periodKey}{p.monthName ? ` · ${p.monthName}` : ''}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {err && <div style={S.errorBox}>⚠ {err}</div>}
      {loading && <div style={S.loadingBox}>Loading…</div>}

      {!loading && analysis && (
        <>
          {/* KPI strip */}
          <div style={S.kpiGrid}>
            <KpiTile label={includeCoS ? 'Total Spend' : 'Total OpEx'}
                     value={fmtMoney(totals.totalSpend)} sub={fmtFull(totals.totalSpend)} />
            <KpiTile label="% of Sales"
                     value={fmtPct(totals.totalSpendPctSales)}
                     sub={includeCoS ? '≤55% · 55-75% · >75%' : '≤15% · 15-25% · >25%'}
                     valueColor={ratioColor(totals.totalSpendPctSales, includeCoS)} />
            <KpiTile label="vs Prior Month"
                     value={fmtDelta(totals.totalSpendDeltaPct)}
                     sub={'Δ ' + fmtMoney(totals.totalSpendDelta)}
                     valueColor={deltaColor(totals.totalSpendDelta)} />
            <KpiTile label="vs Same Month LY"
                     value={fmtDelta(totals.totalSpendVsLyDeltaPct)}
                     sub={analysis.lyPeriodKey ? 'vs ' + analysis.lyPeriodKey : 'no LY data'}
                     valueColor={deltaColor(totals.totalSpendVsLyDelta)} />
            <KpiTile label="Top Category"
                     value={analysis.topCategory ? fmtMoney(analysis.topCategory.amount) : '—'}
                     sub={analysis.topCategory ? analysis.topCategory.reportCategory : '—'} />
          </div>

          {/* Filter chips */}
          {Object.values(filters).some(Boolean) && (
            <div style={S.filterRow}>
              <div style={{ ...S.label, marginBottom: 0 }}>Filters</div>
              {Object.entries(filters).filter(([, v]) => v).map(([k, v]) => (
                <span key={k} style={S.filterChip}>
                  {k}: {v}
                  <button onClick={() => removeFilter(k)} style={{ background: 'transparent', border: 'none', color: 'var(--amber)', cursor: 'pointer', padding: 0, fontSize: 13, lineHeight: 1 }}>×</button>
                </span>
              ))}
              <button onClick={clearFilters} style={{ background: 'transparent', border: 'none', color: 'var(--page-muted)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Clear all</button>
            </div>
          )}

          {/* Composition + Top GL accounts */}
          <div style={S.twoCol}>
            <div style={S.card}>
              <div style={S.cardTitle}>
                Composition by Category
                <span style={S.cardTotal}>{categories.length} categories</span>
              </div>
              <CategoryBars
                categories={categories}
                max={maxCat}
                palette={CAT_COLORS}
                onClick={(c) => setFilters(f => ({ ...f, reportCategory: c.reportCategory }))}
              />
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--page-muted)', marginTop: 10, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                Click a category to drill · % shown is of Sales · Δ is vs prior month
              </div>
            </div>
            <div style={S.card}>
              <div style={S.cardTitle}>
                Top 10 GL Accounts
                <span style={S.cardTotal}>{visibleAccounts.length} total</span>
              </div>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>GL</th>
                    <th style={S.th}>Description</th>
                    <th style={{ ...S.th, ...S.thRight }}>Amount</th>
                    <th style={{ ...S.th, ...S.thRight }}>% Sales</th>
                    <th style={{ ...S.th, ...S.thRight }}>Δ Prior</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleAccounts.slice(0, 10).map(a => (
                    <tr
                      key={a.glAccountKey}
                      onClick={() => setFilters(f => ({ ...f, glAccountKey: a.glAccountKey }))}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(200,149,32,0.04)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ ...S.td, ...S.tdMono }}>{a.glAccount}</td>
                      <td style={S.td} title={a.glDescription}>
                        {(a.glDescription || '').length > 32 ? a.glDescription.slice(0, 32) + '…' : a.glDescription}
                      </td>
                      <td style={{ ...S.td, ...S.tdRight }}>{fmtMoney(a.amount)}</td>
                      <td style={{ ...S.td, ...S.tdRight }}>{fmtPct(a.pctOfSales)}</td>
                      <td style={{ ...S.td, ...S.tdRight, color: deltaColor(a.delta) }}>{fmtDelta(a.deltaPct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 12-month trend */}
          <div style={S.card}>
            <div style={S.cardTitle}>
              12-Month Trend — Top Categories
              <span style={S.cardTotal}>{trend?.trend?.length || 0} rows</span>
            </div>
            {trend && (
              <TrendChart
                trend={trend.trend}
                revenue={trend.revenue}
                palette={CAT_COLORS}
                topCategories={topCats}
              />
            )}
          </div>

          {/* Entity table */}
          <div style={S.card}>
            <div style={S.cardTitle}>
              Per-Entity Spend Ratio
              <span style={S.cardTotal}>
                {entityRows.length} entities · median {fmtPct(medianRatio)}
              </span>
            </div>
            {entityRows.length === 0 ? (
              <div style={S.loadingBox}>No entity data for this period.</div>
            ) : (
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Entity</th>
                    <th style={S.th}>Brand</th>
                    <th style={S.th}>Type</th>
                    <th style={S.th}>Country</th>
                    <th style={{ ...S.th, ...S.thRight }}>Sales</th>
                    <th style={{ ...S.th, ...S.thRight }}>Spend</th>
                    <th style={{ ...S.th, ...S.thRight }}>Spend / Sales</th>
                    <th style={S.th}>RAG</th>
                  </tr>
                </thead>
                <tbody>
                  {entityRows.slice(0, 60).map(e => {
                    const ragColor = ratioVsMedianColor(e.ratio, medianRatio);
                    const barW = e.ratio == null ? 0 : Math.min(100, e.ratio * 100);
                    return (
                      <tr
                        key={e.entityKey}
                        onClick={() => openEntityDrill(e)}
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={ev => ev.currentTarget.style.background = 'rgba(200,149,32,0.04)'}
                        onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}
                      >
                        <td style={S.td}>{e.legalName}</td>
                        <td style={{ ...S.td, ...S.tdMono }}>{e.brand || '—'}</td>
                        <td style={{ ...S.td, ...S.tdMono }}>{e.entityType || '—'}</td>
                        <td style={{ ...S.td, ...S.tdMono }}>{e.country || '—'}</td>
                        <td style={{ ...S.td, ...S.tdRight }}>{fmtMoney(e.sales)}</td>
                        <td style={{ ...S.td, ...S.tdRight }}>{fmtMoney(e.spend)}</td>
                        <td style={{ ...S.td, ...S.tdRight, color: ratioColor(e.ratio, includeCoS) }}>
                          {fmtPct(e.ratio)}
                          {e.ratio != null && (
                            <span style={{ ...S.miniBar, width: barW + 'px', background: ragColor, opacity: 0.6 }} />
                          )}
                        </td>
                        <td style={S.td}>
                          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: ragColor }} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {entityRows.length > 60 && (
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--page-muted)', marginTop: 10, textAlign: 'center' }}>
                Showing top 60 of {entityRows.length} · narrow with filters above
              </div>
            )}
          </div>

          {/* Entity drill */}
          {drill && (
            <div style={S.card}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div>
                  <div style={S.cardTitle}>{drill.entity.legalName} — Expense Lines</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)' }}>
                    {drill.entity.brand} · {drill.entity.entityType} · Sales {fmtMoney(drill.entity.sales)} · Spend {fmtMoney(drill.entity.spend)} · Ratio {fmtPct(drill.entity.ratio)}
                  </div>
                </div>
                <button style={S.closeBtn} onClick={() => setDrill(null)}>✕ Close</button>
              </div>
              {drill.error ? (
                <div style={S.errorBox}>⚠ {drill.error}</div>
              ) : !drill.lines ? (
                <div style={S.loadingBox}>Loading…</div>
              ) : drill.lines.length === 0 ? (
                <div style={S.loadingBox}>No expense lines for this entity in {period}.</div>
              ) : (
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Type</th>
                      <th style={S.th}>Category</th>
                      <th style={S.th}>GL</th>
                      <th style={S.th}>Description</th>
                      <th style={{ ...S.th, ...S.thRight }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drill.lines.map(l => (
                      <tr key={l.glAccount}>
                        <td style={{ ...S.td, ...S.tdMono }}>{l.accountType}</td>
                        <td style={S.td}>{l.reportCategory}</td>
                        <td style={{ ...S.td, ...S.tdMono }}>{l.glAccount}</td>
                        <td style={S.td}>{l.glDescription}</td>
                        <td style={{ ...S.td, ...S.tdRight }}>{fmtMoney(l.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function KpiTile({ label, value, sub, valueColor }) {
  return (
    <div style={S.kpiCard}>
      <div style={S.kpiLabel}>{label}</div>
      <div style={{ ...S.kpiVal, ...(valueColor ? { color: valueColor } : {}) }}>{value}</div>
      {sub && <div style={S.kpiSub}>{sub}</div>}
    </div>
  );
}
